/**
 * src/shell/identity/UserAvatar.tsx
 *
 * Reusable, accessible avatar component supporting Photo Uploads,
 * Preset Illustrations, and WCAG AA-compliant Monograms ([32%, 48%] lightness).
 */

import React, { useState } from 'react';
import type { ExpertiseLevel } from '../../identity/domain/reputation-policy.js';
import type { BadgeRecord } from '../../identity/domain/badge-records.js';
import { MonogramContrastGenerator } from './services/MonogramContrastGenerator';
import { AvatarType } from './types/user-profile';
import { getCategoryVectorIcon } from './category-icons.js';
export { getCategoryVectorIcon };

export interface UserAvatarProps {
  readonly principalId: string;
  readonly displayName?: string;
  readonly avatarUrl?: string;
  readonly avatarType?: AvatarType;
  readonly size?: 'sm' | 'md' | 'lg' | 'xl';
  readonly expertiseLevel?: ExpertiseLevel;
  readonly topBadges?: readonly BadgeRecord[];
  readonly status?: 'online' | 'offline' | 'away';
  readonly profileCompleteness?: number; // 0-100
  readonly showBadgePips?: boolean;
  readonly enableHoverOrbit?: boolean;
  readonly onClick?: () => void;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

const SIZE_MAP = {
  sm: { px: 32, fontSize: '11px', pip: 10, ringWidth: 2 },
  md: { px: 40, fontSize: '13px', pip: 12, ringWidth: 2 },
  lg: { px: 56, fontSize: '18px', pip: 14, ringWidth: 3 },
  xl: { px: 80, fontSize: '24px', pip: 16, ringWidth: 4 },
};

const EXPERTISE_BORDER_COLOR: Record<ExpertiseLevel, { border: string; glow: string; style: string }> = {
  'insufficient-evidence': { border: 'rgba(255, 255, 255, 0.15)', glow: 'none', style: 'dashed' },
  'emerging': { border: 'rgba(56, 189, 248, 0.8)', glow: '0 0 8px rgba(56, 189, 248, 0.3)', style: 'solid' },
  'established': { border: 'rgba(34, 211, 238, 0.9)', glow: '0 0 10px rgba(34, 211, 238, 0.35)', style: 'solid' },
  'advanced': { border: 'rgba(96, 165, 250, 0.95)', glow: '0 0 12px rgba(96, 165, 250, 0.4)', style: 'solid' },
  'expert': { border: 'rgba(129, 140, 248, 1)', glow: '0 0 14px rgba(129, 140, 248, 0.5)', style: 'solid' },
};

const STATUS_COLOR_MAP = {
  online: '#34d399',
  away: '#fbbf24',
  offline: '#64748b',
};

export const UserAvatar: React.FC<UserAvatarProps> = ({
  principalId,
  displayName,
  avatarUrl,
  avatarType = 'photo',
  size = 'md',
  expertiseLevel,
  status,
  onClick,
  className = '',
  style,
}) => {
  const [imageError, setImageError] = useState(false);
  const dim = SIZE_MAP[size];
  const monogram = MonogramContrastGenerator.generate(principalId, displayName);

  const expertiseStyle = expertiseLevel ? EXPERTISE_BORDER_COLOR[expertiseLevel] : null;

  const expertiseLabel = expertiseLevel ? `, ${expertiseLevel} level` : '';
  const statusLabel = status ? `, ${status}` : '';
  const fullLabel = `Avatar for ${displayName || principalId}${expertiseLabel}${statusLabel}`;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : 'img'}
      className={`relative inline-flex items-center justify-center flex-shrink-0 select-none ${onClick ? 'cursor-pointer hover:opacity-90' : ''} ${className}`}
      style={{
        width: dim.px,
        height: dim.px,
        ...style,
      }}
      aria-label={fullLabel}
    >
      <div
        className="w-full h-full rounded-full overflow-hidden flex items-center justify-center shadow-md transition-transform"
        style={{
          border: expertiseStyle ? `${dim.ringWidth}px ${expertiseStyle.style} ${expertiseStyle.border}` : '1px solid rgba(255,255,255,0.12)',
          boxShadow: expertiseStyle ? expertiseStyle.glow : undefined,
          backgroundColor: monogram.backgroundColor,
        }}
      >
        {avatarUrl && !imageError && avatarType !== 'initials' ? (
          <img
            src={avatarUrl}
            alt={displayName || principalId}
            onError={() => setImageError(true)}
            className="w-full h-full object-cover rounded-full"
            loading="lazy"
          />
        ) : (
          <span
            className="font-mono font-bold uppercase tracking-wider"
            style={{
              fontSize: dim.fontSize,
              color: monogram.textColor,
            }}
          >
            {monogram.initials}
          </span>
        )}
      </div>

      {status && (
        <span
          className="absolute bottom-0 right-0 rounded-full border-2 border-slate-900 shadow-sm"
          style={{
            width: dim.pip,
            height: dim.pip,
            backgroundColor: STATUS_COLOR_MAP[status],
          }}
          title={`Status: ${status}`}
        />
      )}
    </div>
  );
};
